import type { ScaleQ } from "../../types/quiz";

// Import SVGs as modules (Vite will optimize them)
import riskAppetite from "../../assets/images/risk_appetite.svg";
import interactionStyle from "../../assets/images/interaction_style.svg";

export type ScaleProps = {
  q: ScaleQ;
  value: number;
  onChange: (v: number) => void;
};

// appetite-1.svg ... appetite-5.svg
const APPETITE_SET = new Map(
    Array.from({length: 5}, (_, i) => [i + 1, `icons/appetite-${i + 1}.svg`])
);

// interaction_style-1.svg ... interaction_style-5.svg
const INTERACTION_SET = new Map(
    Array.from({length: 5}, (_, i) => [
      i + 1,
      `icons/interaction_style-${i + 1}.svg`,
    ])
);

/* ---- category → image mapping (fallbacks) ---- */
const SCALE_ART: Record<string, string> = {
  "RISK APPETITE": riskAppetite,
  "INTERACTION STYLE": interactionStyle,
};

/* ---- NEW: category → series map ---- */
const SERIES_BY_CATEGORY: Record<string, Map<number, string>> = {
  "RISK APPETITE": APPETITE_SET,
  "INTERACTION STYLE": INTERACTION_SET,
};

// /* CHANGE 1: Define thumb width as a constant for clarity and reuse */
const THUMB_WIDTH_PX = 28;

export function Scale({ q, value, onChange }: ScaleProps) {
  const min = q.scale.min;
  const max = q.scale.max;
  const count = max - min + 1;
  const labels = q.scale.options ?? [];
  const idx = Math.min(Math.max(value - min, 0), count - 1);

  /* choose image 1..5 based on the current value */
  const series = SERIES_BY_CATEGORY[q.category];
  const level = (() => {
    if (max === min) return 1;
    const t = (value - min) / (max - min); // 0..1
    const bucket = Math.round(t * 4) + 1; // 1..5
    return Math.min(5, Math.max(1, bucket));
  })();
  const dynamicSrc = series?.get(level);
  const artSrc = dynamicSrc ?? SCALE_ART[q.category];

  return (
      <div
          className="cq-w-full cq-pb-24"
          // Define the thumb width as a CSS custom property for child elements to use
          style={{"--thumb-width": `${THUMB_WIDTH_PX}px`} as React.CSSProperties}
      >
        {artSrc && (
            <div className="cq-flex">
              <img
                  src={`${import.meta.env.BASE_URL}${artSrc}`}
                  alt={`${q.category} scale`}
                  className="cq-mx-auto cq-shrink-0 cq-w-32 md:cq-w-52"
              />
            </div>
        )}

        {labels.length === count && (
            <div className="cq-mb-6 cq-flex cq-items-center cq-justify-between cq-gap-4">
              <div
                  className="cq-w-full cq-p-4 md:cq-p-6 cq-text-center cq-text-black cq-font-medium "
                  aria-live="polite"
                  aria-atomic="true"
              >
                <div className="cq-text-lg">{labels[idx]}</div>
              </div>
            </div>
        )}

        <div className="cq-relative cq-bg-[#f4f4f5] cq-rounded-full">
          {/* ticks */}
          <div className="cq-absolute cq-top-0 cq-flex cq-justify-between cq-w-full cq-h-4 cq-px-8" style={{boxSizing: 'border-box'}}>
            {Array.from({length: count}).map((_, i) => {
              const n = min + i;
              const label = n === 1 ? "1 Low" : n === 5 ? "5 High" : n;
              return <div key={n} data-value={n} className="range-input__label cq-w-2 cq-bg-[#00BBFF] cq-text-[1rem] cq-p-0 cq-text-sm" style={{'--data-label': `"${label}"`}}></div>;
            })}
          </div>
          {/* slider */}
          <input
              type="range"
              min={min}
              max={max}
              step={1}
              value={value}
              onChange={(e) => onChange(Number((e.target as HTMLInputElement).value))}
              className="cq-h-4 cq-px-4"
              style={{overscrollBehavior: "contain", touchAction: "none"}}
          />
        </div>
      </div>
  );
}

export default Scale;